import { Component, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { NgForm, UntypedFormControl } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { GlobalMessageService, GlobalMessageType, Product, TranslationService } from '@spartacus/core';
import { Observable, Subscription } from 'rxjs';
import { filter, first, switchMap, tap } from 'rxjs/operators';
import { ManufacturerCampaignResultsData, ManufacturerCampaignSalesNotification} from '../../custom-manufacturer-campaign.models';
import { deleteSalesNotification, deleteSalesNotificationReset, getManufacturerCampaignDetails, updateSalesNotification, updateSalesNotificationReset } from '../../store/custom-manufacturer-campaign.actions';
import { CustomManufacturerCampaignState } from '../../store/custom-manufacturer-campaign.reducer';
import { deleteSalesNotificationFailure, deleteSalesNotificationSuccess, errorManufacturerCampaignDetails, selectManufacturerCampaignDetails, updateSalesNotificationFailure, updateSalesNotificationFailureMessage, updateSalesNotificationSuccess } from '../../store/custom-manufacturer-campaign.selectors';

@Component({
    selector: 'app-custom-manufacturer-campaign-details',
    templateUrl: './custom-manufacturer-campaign-details.component.html',
    styleUrls: ['./custom-manufacturer-campaign-details.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class CustomManufacturerCampaignDetailsComponent implements OnInit {

  @Input() campaignId?: string;
  @Input() manufacturer?: string;
  @Input() productCode?: string;
  @Input() productName?: string;
  @Input() productEan?: string;
  @Input() supplierArticleNumber?: string;
  @Input() expired?: boolean;
  @Input() startedValidity?: boolean;
  @Input() campaignName?: string;
  @Input() campaignCode?:string;
  @Input() noSerialNumberCheck?: boolean;
  @Input() numberOfCombinations?: string;
  @Input() combiProducts?: Product[];
  @Input() combiValue1?: string;
  @Input() campaignCodeInput?:boolean;
  @ViewChild('updateForm') updateForm: NgForm;

  serialNumbersToBeDeleted: Array<string> = new Array();
  addRow = false;
  newSerialNumber: string;
  newCampaignCode: string;
  newSalesDate: Date;
  lastDeletedIndex: number;
  emptyTable: boolean;
  newCombiProduct_1: string;
  newCombiProductSerialNo_1: string;
  newCombiProduct_2: string;
  newCombiProductSerialNo_2: string;
  handleUpdateErrorResult: Subscription;
  newTradeInSerialNumber: string;
  newTradeInName: string;
  manufacturerCampaignDetails: ManufacturerCampaignSalesNotification[];
  errorDetails: Error;
  isRequired = true;
  disableSaveButton = true;
  


  manufacturerCampaignDetails$: Observable<ManufacturerCampaignSalesNotification[]> =
    this.store.select(selectManufacturerCampaignDetails);
  errorDetails$: Observable<Error> = this.store.select(errorManufacturerCampaignDetails);
  deleteSuccess$: Observable<boolean> = this.store.select(deleteSalesNotificationSuccess);
  deleteFailure$: Observable<boolean> = this.store.select(deleteSalesNotificationFailure);
  updateSuccess$: Observable<boolean> = this.store.select(updateSalesNotificationSuccess);
  updateFailure$: Observable<boolean> = this.store.select(updateSalesNotificationFailure);
  updateFailureMessage$: Observable<ManufacturerCampaignResultsData[]> = this.store.select(updateSalesNotificationFailureMessage);

  constructor(
    public modal: NgbActiveModal,
    private store: Store<CustomManufacturerCampaignState>,
    private translation: TranslationService,
    private messageService: GlobalMessageService
  ) { }

  private errorMessageKeys = {
    successfulEntries : 'customManufacturerCampaign.import.success',
    successfulButUnauthorizedEntries:"customManufacturerCampaign.import.successfulButUnauthorized",
    hasInvalidRecords : 'customManufacturerCampaign.import.errors.invalidEntries',
    hasDuplicates : 'customManufacturerCampaign.import.errors.hasDuplicates'
  };

  ngOnInit(): void {
    this.handleAddRow();
    this.getManufacturerCampaignDetails();

    this.manufacturerCampaignDetails$
      .pipe(
        filter((manufacturerCampaignDetails) => manufacturerCampaignDetails != null),
        tap((manufacturerCampaignDetails) => {
          this.manufacturerCampaignDetails = manufacturerCampaignDetails;
          if ((!manufacturerCampaignDetails || manufacturerCampaignDetails.length <= 0) && (this.expired || !this.startedValidity))
          {
            this.emptyTable = true;
          }
        })
      )
      .subscribe();

    this.errorDetails$
      .pipe(
        filter((error) => error != null),
        tap((error) => {
          this.errorDetails = error;
        }),
        switchMap(() => this.translation.translate('customManufacturerCampaign.details.error')),
        tap((text) => {
          this.messageService.add(text, GlobalMessageType.MSG_TYPE_ERROR);
        })
      )
      .subscribe();
    this.handleUpdateSuccess();
    this.handleUpdateErrorResult = this.handleUpdateError();
    this.handleDeletionSuccess();
    this.handleDeletionError();
    this.isCombiProduct2DataRequired();
  }

  ngOnDestroy() {
    if (this.handleUpdateErrorResult){
      this.handleUpdateErrorResult.unsubscribe();
    }
  }

  getManufacturerCampaignDetails(): void {
    this.manufacturerCampaignDetails = null;
    this.store.dispatch(getManufacturerCampaignDetails(
      {
        campaignId: this.campaignId,
        productCode: this.productCode
      }
    ));
  }

  onAddSalesNotification(): void {
    const newSalesNotificationData = this.createSalesNotificationData(
      this.updateForm.value.newSerialNumber,
      this.updateForm.value.newCampaignCode,
      this.updateForm.value.newSalesDate,
      this.updateForm.value.newSalesDate,
      null,
      this.updateForm.value.newTradeInSerialNumber,
      this.updateForm.value.newTradeInName,
      this.updateForm.value.newCombiProduct_1,
      this.updateForm.value.newCombiProductSerialNo_1,
      this.updateForm.value.newCombiProduct_2,
      this.updateForm.value.newCombiProductSerialNo_2
      );
      
    this.createFormControls();
    this.manufacturerCampaignDetails = Object.assign([], this.manufacturerCampaignDetails);
    this.manufacturerCampaignDetails.push(newSalesNotificationData);
    this.newSerialNumber = ' ';
    this.newCampaignCode= ' ';
    this.newTradeInSerialNumber = ' ';
    this.newTradeInName = ' ';
    this.newCombiProductSerialNo_1 = ' ';
    this.newCombiProduct_1 = ' ';
    this.newCombiProductSerialNo_2 = ' ';
    this.newCombiProduct_2 = ' ';
    this.newSalesDate = null;
    this.handleSaveButton();
  }

  onSave(): void {
    if (this.serialNumbersToBeDeleted && this.serialNumbersToBeDeleted.length > 0) {
      this.store.dispatch(deleteSalesNotification({
        salesNotificationPkList: this.serialNumbersToBeDeleted,
      }));
    }
    this.updateSalesNotifications();
  }

  updateSalesNotifications(): void {
    const count = this.manufacturerCampaignDetails.length;
    const salesNotifications: Array<ManufacturerCampaignSalesNotification> = new Array();
    let i = 0;
    while (i <= count) {
      if (this.updateForm.controls['updateSerialNumber[' + i + ']'] != null
        && this.manufacturerCampaignDetails[i]) {
        const newSalesNotificationData = this.createSalesNotificationData(
          this.updateForm.controls['updateSerialNumber[' + i + ']'].value,
          this.updateForm.controls['updateCampaignCode[' + i + ']']?.value,
          this.updateForm.controls['updateSalesDate[' + i + ']'].value,
          this.updateForm.controls['updateSalesDate[' + i + ']'].value,
          this.manufacturerCampaignDetails[i].pk,
          this.updateForm.controls['updateTradeInSerialNumber[' + i + ']']?.value,
          this.updateForm.controls['updateTradeInName[' + i + ']']?.value,
          this.updateForm.controls['updateCombiProduct_1[' + i + ']']?.value,
          this.updateForm.controls['updateCombiProductSerialNo_1[' + i + ']']?.value,
          this.updateForm.controls['updateCombiProduct_2[' + i + ']']?.value,
          this.updateForm.controls['updateCombiProductSerialNo_2[' + i + ']']?.value
          );
        salesNotifications.push(newSalesNotificationData);
      }
      i += 1;
    }
    this.store.dispatch(updateSalesNotification({
      salesNotifications
    }));
    this.manufacturerCampaignDetails = null;
    this.errorDetails = null;
  }

  onDeleteSerialNumber(index: number, pk: string): void {
    this.manufacturerCampaignDetails = Object.assign([], this.manufacturerCampaignDetails);
    this.manufacturerCampaignDetails[index] = null;
    this.serialNumbersToBeDeleted.push(pk);
    this.lastDeletedIndex = index;
    this.handleSaveButton();
  }

  handleAddRow(): void {
    if (!this.expired) {
      this.addRow = true;
    }
  }

  createSalesNotificationData(newSerialNumber?: string,newCampaignCode?: string, newSalesDate?: Date, formattedSalesDate?: string, pk?: string,
                              newTradeInSerialNumber?: string, newTradeInName?: string, newCombiProduct_1?: string, newCombiProductSerialNo_1?: string,
                              newCombiProduct_2?: string, newCombiProductSerialNo_2?: string): ManufacturerCampaignSalesNotification {
    if (!this.campaignCodeInput) {
      newCampaignCode = '';
    }
    const newSalesNotificationData = {} as ManufacturerCampaignSalesNotification;
    newSalesNotificationData.campaignId = this.campaignId;
    newSalesNotificationData.productCode = this.productCode;
    newSalesNotificationData.serialNr = newSerialNumber;
    newSalesNotificationData.campaignCode = newCampaignCode;
    newSalesNotificationData.salesDate = newSalesDate;
    newSalesNotificationData.formattedSalesDate = formattedSalesDate;
    newSalesNotificationData.pk = pk;
    newSalesNotificationData.tradeInSerialNo = newTradeInSerialNumber;
    newSalesNotificationData.tradeInName = newTradeInName;
    const combiProduct_1 = {} as Product;
    combiProduct_1.code = newCombiProduct_1;
    newSalesNotificationData.combiProduct_1 = combiProduct_1;
    newSalesNotificationData.combiProductSerialNo_1 = newCombiProductSerialNo_1;
    const combiProduct_2 = {} as Product;
    combiProduct_2.code = newCombiProduct_2;
    newSalesNotificationData.combiProduct_2 = combiProduct_2;
    newSalesNotificationData.combiProductSerialNo_2 = newCombiProductSerialNo_2;
    newSalesNotificationData.entryStatus = 'OPEN';

    return newSalesNotificationData;
  }

  handleDeletionSuccess() {
    this.deleteSuccess$
      .pipe(
        filter((success) => success == true),
        tap(() => {
          this.translation
            .translate('customManufacturerCampaign.messages.deleteSuccess')
            .pipe(first())
            .subscribe((text) => {
              this.messageService.add(text, GlobalMessageType.MSG_TYPE_CONFIRMATION);
              this.store.dispatch(deleteSalesNotificationReset());
              this.modal.close();
            });
        })
      )
      .subscribe();
  }

  handleDeletionError() {
    this.deleteFailure$
      .pipe(
        filter((failure) => failure && failure == true),
        tap(() => {
          this.translation
            .translate('customManufacturerCampaign.messages.deleteFailure')
            .pipe(first())
            .subscribe((text) => {
              this.messageService.add(text, GlobalMessageType.MSG_TYPE_ERROR);
              this.store.dispatch(deleteSalesNotificationReset());
              this.modal.close();
              document.getElementById('manufacturer-campaign-filter').scrollIntoView();
            });
        })
      )
      .subscribe();
  }

  handleUpdateSuccess() {
    this.updateSuccess$
      .pipe(
        filter((success) => success && success == true),
        tap(() => {
              this.store.dispatch(updateSalesNotificationReset());
              this.modal.close();
        })
      )
      .subscribe();
  }

  handleUpdateError() {
    let errorMessages: ManufacturerCampaignResultsData[];
    return this.updateFailure$
      .pipe(
        filter((failure) => failure && failure == true),
        tap(() => {
          this.updateFailureMessage$.pipe(
            filter((message) => message != null && message.length > 0),
            tap<ManufacturerCampaignResultsData[]>((message) => {
              errorMessages = message;
            })
          )
          .subscribe();
          const SHOW_MSG_TIMEOUT_INFO = 600000; // 10 minutes

          for (const errorMessage of errorMessages) {
            this.translation
            .translate(this.errorMessageKeys[errorMessage.message])
            .pipe(first())
            .subscribe((text) => {
              const finaltext = `${text} ${errorMessage.invalidItems ? '<br/>' + errorMessage.invalidItems.join('<br/>'): errorMessage.ignoredCampaignCode?'<br/>' + errorMessage.ignoredCampaignCode.join('<br/>') : ''}`;
              this.messageService.add(finaltext, GlobalMessageType.MSG_TYPE_ERROR, SHOW_MSG_TIMEOUT_INFO);
            });
          }
          this.store.dispatch(updateSalesNotificationReset());
          this.modal.close();
          document.getElementById('manufacturer-campaign-filter').scrollIntoView();
        })
      )
      .subscribe();
  }

  createFormControls() {
    this.updateForm.control.addControl('updateSerialNumber[' + this.lastDeletedIndex + ']',
        new UntypedFormControl(this.updateForm.value.newSerialNumber));
        this.updateForm.control.addControl('updateCampaignCode[' + this.lastDeletedIndex + ']',
          new UntypedFormControl(this.updateForm.value.newCampaignCode));
    this.updateForm.control.addControl('updateSalesDate[' + this.lastDeletedIndex + ']',
        new UntypedFormControl(this.updateForm.value.newSalesDate));
    this.updateForm.control.addControl('updateTradeInSerialNumber[' + this.lastDeletedIndex + ']',
        new UntypedFormControl(this.updateForm.value.newTradeInSerialNumber));
    this.updateForm.control.addControl('updateTradeInName[' + this.lastDeletedIndex + ']',
        new UntypedFormControl(this.updateForm.value.newTradeInName));
    this.updateForm.control.addControl('updateCombiProduct_1[' + this.lastDeletedIndex + ']',
        new UntypedFormControl(this.updateForm.value.newCombiProduct_1));
    this.updateForm.control.addControl('updateCombiProductSerialNo_1[' + this.lastDeletedIndex + ']',
        new UntypedFormControl(this.updateForm.value.newCombiProductSerialNo_1));
    this.updateForm.control.addControl('updateCombiProduct_2[' + this.lastDeletedIndex + ']',
        new UntypedFormControl(this.updateForm.value.newCombiProduct_2));
    this.updateForm.control.addControl('updateCombiProductSerialNo_2[' + this.lastDeletedIndex + ']',
        new UntypedFormControl(this.updateForm.value.newCombiProductSerialNo_2));
  }

  isCombiProduct2DataRequired(){
    if (this.combiValue1)
    {
      this.isRequired = false;
    }
  }

  handleSaveButton(){
    const manufacturerCampaigns = this.manufacturerCampaignDetails.filter((manufacturerCampaign) => {
      return manufacturerCampaign !== null;
    });
    const serialNumbersToBeDeleted = this.serialNumbersToBeDeleted.filter((serialNumberToBeDeleted) => {
      return serialNumberToBeDeleted !== null;
    });
    if(manufacturerCampaigns.length > 0 || serialNumbersToBeDeleted.length > 0){
      this.disableSaveButton = false;
    } else {
      this.disableSaveButton = true;
    }
  }

  modifiedEntry(){
    this.disableSaveButton = false;
  }

  isEntryOpen(manufacturerCampaignNotification?:ManufacturerCampaignSalesNotification) {
    return manufacturerCampaignNotification?.entryStatus === 'OPEN';
  }
}
